import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ArmyBuilderConfig } from '../../global/config';
import { SettingsService } from '../../global/settings/service/settings.service';
import { PDFSaveService } from './pdf-save.service';

export interface PdfExportOptions {
    includePageNumbers?: boolean;
    fontSize?: string;
    scale?: number;
    viewportWidth?: number;
    noMargin?: boolean;
    /** Plain text shown at the top of every sheet. Rendered by puppeteer, not by the page. */
    runningHead?: string;
    /** Classes applied to the root of the *serialized clone*, never to the live element -- so
     *  the on-screen preview does not reflow while the export is in flight. */
    exportClasses?: string[];
    /**
     * CSS appended verbatim to the exported document, after the collected stylesheets and exempt
     * from the `@page` / `@media print` filtering applied to them.
     *
     * This exists for page geometry the app document deliberately does not carry. The rulebook's
     * `@page` rules -- sheet size, mirrored margins, running head, folio, background -- would
     * otherwise have to be linked into the app just to be collected, where they would alter the
     * browser's own Ctrl+P for every page of the app and do nothing useful on screen. Passing them
     * only at export keeps them where they belong.
     */
    extraCss?: string;
    /** Run paged.js server-side before printing. Required for running heads, folios and mirrored
     *  margins; see PdfGenerateOptions.paginate in the API. */
    paginate?: boolean;
    /**
     * Export with ONLY `extraCss`, discarding the app's own stylesheets.
     *
     * Normally an export wants the app's CSS, because that is what makes it look like the thing on
     * screen. A paginated export wants the opposite: paged.js measures the boxes it builds, and the
     * app's global CSS -- Ionic's resets and the army list's flex desk in particular -- distorts
     * those measurements enough to stop its layout loop completing, so it produces a page or two
     * of nothing instead of the whole book. Sending only the stylesheets the document actually
     * needs is what makes server-side pagination behave the way it does in isolation.
     */
    onlyExtraCss?: boolean;
}

/**
 * Renders a live DOM element to a PDF via the API's puppeteer endpoint.
 *
 * Consolidates the `serializeHtmlForPdf` helper that had been copy-pasted into three
 * page components. New callers should use this; the existing three still carry their
 * own copies and can be migrated separately.
 */
@Injectable({ providedIn: 'root' })
export class PdfExportService {
    private settingsService = inject(SettingsService);
    private appConfig = inject(ArmyBuilderConfig);
    private pdfSaveService = inject(PDFSaveService);

    async exportElement(element: HTMLElement, filename: string, options: PdfExportOptions = {}): Promise<void> {
        const blob = await this.renderElement(element, options, filename);
        await this.pdfSaveService.savePDF(blob, filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
    }

    /**
     * Renders the PDF and hands back the blob instead of saving it.
     *
     * Split out of `exportElement` for callers that want the document for something other than a
     * download -- the rulebook shows it inline as its preview, so that what the reader sees is the
     * exported artefact itself rather than a second rendering that can drift from it.
     */
    async renderElement(element: HTMLElement, options: PdfExportOptions = {}, filename = 'document'): Promise<Blob> {
        const html = await this.serializeHtmlForPdf(element, options.exportClasses, options.extraCss, options.onlyExtraCss, options.paginate);
        const login = await firstValueFrom(this.settingsService.login$);
        const token = (login as any)?.user?.accessToken;

        const response = await fetch(`${this.appConfig.apiBaseUrl}/pdf/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ html, filename, options }),
            signal: AbortSignal.timeout(180000)
        });

        if (!response.ok) {
            throw new Error(`PDF generation failed (${response.status})`);
        }

        return response.blob();
    }

    /** Matches `url(foo)`, `url('foo')` and `url("foo")`. */
    private static readonly CSS_URL = /url\((['"]?)([^'")]+)\1\)/g;

    /** Fetches one asset and returns it as a data URI, or null if it cannot be had. */
    private async toDataUri(url: string): Promise<string | null> {
        try {
            const res = await fetch(url);
            if (!res.ok) return null;
            const bytes = new Uint8Array(await res.arrayBuffer());
            // Chunked so a 500KB font does not blow the argument limit of String.fromCharCode.
            let binary = '';
            const CHUNK = 0x8000;
            for (let i = 0; i < bytes.length; i += CHUNK) {
                binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
            }
            const mime = (res.headers.get('content-type') || 'application/octet-stream').split(';')[0];
            return `data:${mime};base64,${btoa(binary)}`;
        } catch {
            return null;
        }
    }

    /**
     * Rewrites every resolvable `url(...)` in the collected CSS to a data URI.
     *
     * Load-bearing: `PdfService` calls `page.setContent()` with no base URL, so a relative
     * reference cannot resolve inside puppeteer -- fonts substitute and backgrounds vanish,
     * silently, in the PDF only. Cross-origin URLs are skipped because they cannot be read
     * back reliably, and leaving them alone is no worse than the status quo.
     */
    private async inlineCssUrls(css: string): Promise<string> {
        const wanted = new Set<string>();
        // `matchAll` is ES2020 and the antares app targets es2019/es2015 (see tsconfig `lib`), so
        // scan with a stateful `exec` loop instead. Use a fresh regex instance -- `exec` mutates
        // `lastIndex` on whatever instance it is called on, and CSS_URL is also reused below by
        // `.replace()`. Sharing one instance between the two would leave `lastIndex` non-zero
        // after this loop and silently skip matches on the replace pass (or on a second call).
        const scanner = new RegExp(PdfExportService.CSS_URL.source, 'g');
        let match: RegExpExecArray | null;
        while ((match = scanner.exec(css)) !== null) {
            const raw = match[2].trim();
            if (!raw || raw.startsWith('data:') || raw.startsWith('#')) continue;
            try {
                const resolved = new URL(raw, document.baseURI);
                if (resolved.origin !== location.origin) continue;
                wanted.add(raw);
            } catch {
                // Genuinely malformed URL (fails the URL constructor). Leave it.
            }
        }

        const resolved = new Map<string, string>();
        await Promise.all(
            [...wanted].map(async (raw) => {
                const uri = await this.toDataUri(new URL(raw, document.baseURI).href);
                if (uri) resolved.set(raw, uri);
                else console.warn(`[pdf-export] could not inline ${raw}; it will be missing from the PDF`);
            })
        );

        return css.replace(PdfExportService.CSS_URL, (whole, _quote, raw) => {
            const uri = resolved.get(String(raw).trim());
            return uri ? `url("${uri}")` : whole;
        });
    }

    /**
     * Drops any `url(...)` that is not already a data URI.
     *
     * Only for paginated exports, and it is not cosmetic. paged.js's chunker awaits
     * `loadFonts()` -- every unloaded `FontFace` in the document -- BEFORE it lays out a single
     * page, and `page.setContent()` gives puppeteer no base URL, so a reference that did not get
     * inlined resolves against nothing and its load promise never settles. The whole render then
     * hangs with zero pages and no error: measured here as 13 pages and 100% of the text with the
     * references removed, versus 0 pages with a single unresolvable `@font-face` or page
     * background left in.
     *
     * Losing a font or a background image costs appearance. Leaving one in costs the document.
     */
    private stripUnresolvableUrls(css: string): string {
        return css.replace(PdfExportService.CSS_URL, (whole, _quote, raw) => {
            const value = String(raw).trim();
            if (value.startsWith('data:')) return whole;
            console.warn(`[pdf-export] dropping un-inlinable ${value} from a paginated export; it would hang the renderer`);
            return 'none';
        });
    }

    /** Clones the element and inlines every `<img>` src on the clone. The live DOM is untouched. */
    private async cloneWithInlinedImages(element: HTMLElement): Promise<HTMLElement> {
        const clone = element.cloneNode(true) as HTMLElement;
        const images = Array.from(clone.querySelectorAll('img'));
        const cache = new Map<string, string | null>();

        await Promise.all(
            images.map(async (img) => {
                const src = img.getAttribute('src');
                if (!src || src.startsWith('data:')) return;
                const absolute = new URL(src, document.baseURI).href;
                if (!cache.has(absolute)) cache.set(absolute, await this.toDataUri(absolute));
                const uri = cache.get(absolute);
                if (uri) img.setAttribute('src', uri);
                else console.warn(`[pdf-export] could not inline image ${src}`);
            })
        );

        return clone;
    }

    private async serializeHtmlForPdf(
        element: HTMLElement,
        exportClasses?: string[],
        extraCss?: string,
        onlyExtraCss = false,
        paginated = false
    ): Promise<string> {
        let css = '';
        // The element's OWN document, not the global one -- for everything in the app these are the
        // same object, but it keeps this correct for an element that lives in a frame.
        const ownerDocument = element.ownerDocument ?? document;
        for (const sheet of onlyExtraCss ? [] : Array.from(ownerDocument.styleSheets)) {
            try {
                css += Array.from((sheet as CSSStyleSheet).cssRules || [])
                    .filter((rule) => {
                        const trimmed = rule.cssText.trimStart();
                        // Deliberately dropped: leaving `@page` / `@media print` rules in makes the
                        // K47 unit-card exporter emit duplicate page breaks (its own `@page` rule
                        // fights the one puppeteer's `page.pdf()` call already applies). This is why
                        // print-affecting styles hang off the `.pdf-export` / `.no-backgrounds`
                        // classes elsewhere in this codebase instead of `@media print`.
                        //
                        // An exporter whose page geometry IS such rules passes them through
                        // `extraCss` instead, which is appended after this filtering.
                        return !trimmed.startsWith('@page') && !trimmed.startsWith('@media print');
                    })
                    .map((rule) => rule.cssText)
                    .join('\n');
            } catch {
                // Cross-origin stylesheet -- unreadable, and not ours. Skip it.
            }
        }

        // `extraCss` goes through the same url() inlining as the collected sheets -- its
        // `background: url(...)` and any @font-face it carries must survive server-side, where
        // `page.setContent()` has no base URL to resolve them against.
        let [inlinedCss, inlinedExtraCss, clone] = await Promise.all([
            this.inlineCssUrls(css),
            extraCss ? this.inlineCssUrls(extraCss) : Promise.resolve(''),
            this.cloneWithInlinedImages(element)
        ]);

        // Anything inlining could not resolve is now a hang risk rather than a cosmetic loss --
        // see stripUnresolvableUrls. Only paginated exports care, because only they run paged.js.
        if (paginated) {
            inlinedCss = this.stripUnresolvableUrls(inlinedCss);
            inlinedExtraCss = this.stripUnresolvableUrls(inlinedExtraCss);
        }

        // Applied to the clone only -- never to `element`, which stays on screen for the
        // duration of the export. See the `exportClasses` doc comment on `PdfExportOptions`.
        for (const className of exportClasses ?? []) {
            clone.classList.add(className);
        }

        // Ionic's global app-shell reset (loaded as part of the collected `css` above) pins
        // `body { position: fixed; width/height: 100%; overflow: hidden; }`. That is fine for a
        // scrolling SPA shell, but puppeteer's `page.setContent()` renders this document standalone:
        // fixed-positioned and exactly one viewport tall means only the first "page" of content is
        // ever visible, so the exported PDF is silently truncated no matter how much content there
        // is. This override must stay ahead of the collected CSS in source order and use
        // `!important`, because Ionic's rule is not itself `!important` and appears later in `css`.
        // The K47 unit-card exporter (`k47-unit-cards.page.ts`), which this service was extracted
        // from, carries the identical override for the same reason -- do not delete this as dead code.
        //
        // `background: #fff` is load-bearing too, found while prototyping the rulebook PDF
        // (docs/superpowers/specs/2026-09-10-antares-he-rulebook-pdf-design.md): neither `html`
        // nor `body` has an explicit background anywhere in the app, so they stay at the CSS
        // initial value (transparent). That is invisible for exports whose content happens to
        // paint every pixel of every page (the army list's full-bleed nebula backgrounds do), but
        // when Chrome's print pagination allocates a page taller than the actual content -- which
        // it does whenever content height isn't an exact multiple of the page height -- the
        // leftover transparent area renders as a solid mid-grey block in the PDF instead of white
        // paper. Verified directly: reproduced with a raw puppeteer script against the exact
        // serialized document, confirmed present with `column-count` and with plain single-column
        // content alike (so it is a general transparency artifact, not a multicol bug), and fixed
        // by this one line with no other change.
        const paginationReset = `html, body {
            height: auto !important;
            overflow: visible !important;
            position: static !important;
            contain: none !important;
            background: #fff !important;
        }`;

        // `hyphens: auto` (see `.unit-page__fluff` in army-list-pdf.scss) is a no-op unless Chrome
        // can resolve the element's language. The exported document is a standalone
        // `page.setContent()` document, not a navigation of `index.html`, so it does not inherit
        // that file's `<html lang="en">` -- without setting it here explicitly, justified fluff
        // text renders with wide unhyphenated gaps in the PDF even though the on-screen preview
        // hyphenates correctly.
        const lang = document.documentElement.lang || 'en';
        // `extraCss` last, so an exporter's own page geometry wins over anything the app's
        // collected stylesheets happen to say.
        return `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><style>${paginationReset}${inlinedCss}${inlinedExtraCss}</style></head><body>${clone.outerHTML}</body></html>`;
    }
}
